import { formatOrgNumber, PROVIDER_DOCUMENT_TYPES, type AdminProvider } from '@hemmafixa/shared';
import {
  Avatar,
  Badge,
  Button,
  Card,
  DescriptionList,
  DetailLayout,
  DetailSection,
  EmptyState,
  FileGallery,
  FormField,
  InlineConfirm,
  InlinePanel,
  InlinePanelFooter,
  StatusBadge,
  Textarea,
  toast,
} from '@hemmafixa/ui';
import { FileText, SearchX } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useI18n } from '@/i18n/context';
import type { Messages } from '@/i18n/sv';
import { ApiError } from '@/lib/api';
import { BackLink } from '../shared/back-link';
import { useCategoryLabel } from '../shared/format';
import { galleryItem, useFileTexts } from '../shared/file-uploads';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { useAdminProvider, useRejectProvider, useVerifyProvider, useAdminProviderDocuments } from './api';
import { AuditList } from './audit-list';

function actionError(error: unknown, t: Messages) {
  if (error instanceof ApiError && error.code === 'CONFLICT') return t.admin.providers.conflict;
  if (error instanceof ApiError && error.status === 0) return t.common.errorMessage;
  return t.admin.providers.genericError;
}

/** One provider profile: check the company, then verify or reject (P2-10). */
export function ProviderPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  return (
    <div className="grid gap-4">
      <BackLink to="/admin/providers" label={t.nav.providers} />
      <AdminProviderDetail id={id} />
    </div>
  );
}

/** Loads and shows one provider; the full page and the list's details panel both use it. */
export function AdminProviderDetail({ id }: { id: string }) {
  const { t } = useI18n();
  const query = useAdminProvider(id);
  return (
    <QueryView query={query} loading="detail">
      {(provider) =>
        !provider ? (
          <EmptyState icon={SearchX} title={t.common.notFoundTitle} description={t.common.notFoundText} />
        ) : (
          <ProviderDetail provider={provider} />
        )
      }
    </QueryView>
  );
}

function ProviderDetail({ provider }: { provider: AdminProvider }) {
  const { t, date } = useI18n();
  const statuses = useStatuses();
  const categoryLabel = useCategoryLabel();
  const [panel, setPanel] = useState<'verify' | 'reject' | null>(null);
  const verify = useVerifyProvider(provider.id);
  const p = t.admin.providers;
  const { user } = provider;

  const runVerify = () =>
    verify.mutate(undefined, {
      onSuccess: () => {
        setPanel(null);
        toast.success(p.verifiedToast);
      },
      onError: (error) => toast.error(actionError(error, t)),
    });

  // Verifying and rejecting open in place of the buttons
  const decision =
    panel === 'verify' ? (
      <InlineConfirm
        open
        onOpenChange={(open) => !open && setPanel(null)}
        title={p.verifyTitle}
        description={p.verifyText}
        confirmLabel={p.verify}
        cancelLabel={t.common.cancel}
        loading={verify.isPending}
        onConfirm={runVerify}
      />
    ) : panel === 'reject' ? (
      <RejectPanel providerId={provider.id} onClose={() => setPanel(null)} />
    ) : provider.status === 'pending' ? null : (
      <p className="text-sm text-muted-foreground">
        {provider.status === 'onboarding'
          ? p.notSubmitted
          : provider.status === 'rejected'
            ? p.waitingAgain
            : p.decided}
      </p>
    );

  const facts = [
    { label: p.columns.status, value: <StatusBadge status={provider.status} statuses={statuses.provider} /> },
    ...(provider.submittedAt ? [{ label: p.submitted, value: date(provider.submittedAt) }] : []),
    ...(provider.verifiedAt ? [{ label: p.verifiedAt, value: date(provider.verifiedAt) }] : []),
    ...(provider.rejectionReason ? [{ label: p.rejectionReason, value: provider.rejectionReason }] : []),
  ];

  return (
    <>
      <Card className="grid gap-4 p-5">
        {/* Name on the left, the key facts beside it, so the card stays short */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          <div className="flex min-w-0 flex-1 basis-64 items-center gap-4">
            <Avatar seed={provider.id} name={provider.companyName ?? ''} kind="company" size="lg" />
            <div className="grid min-w-0 gap-0.5">
              <h1
                className="truncate text-2xl font-bold text-secondary"
                title={provider.companyName ?? undefined}
              >
                {provider.companyName ?? p.noCompanyName}
              </h1>
              {provider.orgNumber && (
                <p className="text-sm text-muted-foreground">{formatOrgNumber(provider.orgNumber)}</p>
              )}
            </div>
          </div>
          <dl className="flex flex-wrap gap-x-8 gap-y-3">
            {facts.map((fact) => (
              <div key={fact.label} className="grid gap-1.5">
                <dt className="text-xs text-muted-foreground">{fact.label}</dt>
                <dd className="text-sm font-medium text-secondary">{fact.value}</dd>
              </div>
            ))}
          </dl>
        </div>
        {provider.status === 'pending' && panel === null && (
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button variant="outline" size="lg" className="text-danger" onClick={() => setPanel('reject')}>
              {p.reject}
            </Button>
            <Button size="lg" className="min-w-40" onClick={() => setPanel('verify')}>
              {p.verify}
            </Button>
          </div>
        )}
        {decision}
      </Card>
      <DetailLayout
        aside={
          <>
            <DetailSection title={p.contact} collapsible>
              <div className="grid gap-3">
                {user.status === 'suspended' && (
                  <Badge tone="danger" className="justify-self-start">
                    {p.accountSuspended}
                  </Badge>
                )}
                <DescriptionList
                  items={[
                    { label: t.admin.users.columns.name, value: user.name },
                    {
                      label: p.email,
                      value: (
                        <a
                          className="break-all font-medium underline underline-offset-2"
                          href={`mailto:${user.email}`}
                        >
                          {user.email}
                        </a>
                      ),
                    },
                    ...(user.phone
                      ? [
                          {
                            label: p.phone,
                            value: (
                              <a
                                className="font-medium underline underline-offset-2"
                                href={`tel:${user.phone}`}
                              >
                                {user.phone}
                              </a>
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
                <Link
                  to={`/admin/users/${user.id}`}
                  className="justify-self-start text-sm font-medium underline underline-offset-2"
                >
                  {p.openAccount}
                </Link>
              </div>
            </DetailSection>
            <DetailSection title={t.provider.onboarding.documentsTitle}>
              <ProviderDocumentList providerId={provider.id} />
            </DetailSection>
            <DetailSection title={p.history}>
              <ol className="grid gap-3 text-sm">
                {provider.history.map((entry, index) => (
                  <li key={index} className="grid gap-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={entry.toStatus} statuses={statuses.provider} />
                      <span className="text-muted-foreground">
                        {entry.actorName ?? p.system} · {date(entry.createdAt)}
                      </span>
                    </div>
                    {entry.note && <p className="text-muted-foreground">{entry.note}</p>}
                  </li>
                ))}
              </ol>
            </DetailSection>
          </>
        }
      >
        <DetailSection title={p.company}>
          <DescriptionList
            items={[
              { label: p.columns.company, value: provider.companyName ?? '–' },
              {
                label: p.columns.orgNumber,
                value: provider.orgNumber ? formatOrgNumber(provider.orgNumber) : '–',
              },
              { label: p.fSkatt, value: provider.hasFSkatt ? p.fSkattConfirmed : p.fSkattMissing },
              ...(provider.website
                ? [
                    {
                      label: p.website,
                      value: (
                        <a
                          className="break-all font-medium underline underline-offset-2"
                          href={provider.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {provider.website}
                        </a>
                      ),
                    },
                  ]
                : []),
            ]}
          />
          {provider.about && (
            <div className="mt-4 grid gap-1">
              <p className="text-sm font-medium">{p.about}</p>
              <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                {provider.about}
              </p>
            </div>
          )}
        </DetailSection>
        <DetailSection title={p.services}>
          <DescriptionList
            items={[
              {
                label: p.categories,
                value: (
                  <div className="flex flex-wrap gap-1">
                    {provider.categorySlugs.map((slug) => (
                      <Badge key={slug}>{categoryLabel(slug)}</Badge>
                    ))}
                  </div>
                ),
              },
              { label: p.counties, value: provider.counties.map((county) => county.name).join(', ') || '–' },
            ]}
          />
        </DetailSection>
        <DetailSection title={p.activity}>
          <AuditList entityType="provider" entityId={provider.id} />
        </DetailSection>
      </DetailLayout>
    </>
  );
}

function RejectPanel({ providerId, onClose }: { providerId: string; onClose: () => void }) {
  const { t } = useI18n();
  const p = t.admin.providers;
  const reject = useRejectProvider(providerId);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (reason.trim().length < 3) {
      setError(p.reasonTooShort);
      return;
    }
    reject.mutate(reason.trim(), {
      onSuccess: () => {
        onClose();
        toast.success(p.rejectedToast);
      },
      onError: (failure) => toast.error(actionError(failure, t)),
    });
  };

  return (
    <InlinePanel
      title={p.rejectTitle}
      description={p.rejectText}
      onClose={onClose}
      onSubmit={submit}
      tone="danger"
      closeLabel={t.common.cancel}
    >
      <FormField label={p.reason} error={error} required>
        <Textarea
          rows={4}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </FormField>
      <InlinePanelFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t.common.cancel}
        </Button>
        <Button type="submit" variant="danger" loading={reject.isPending}>
          {p.reject}
        </Button>
      </InlinePanelFooter>
    </InlinePanel>
  );
}

/** The provider's checked documents by type, to verify the registration and F-skatt. */
function ProviderDocumentList({ providerId }: { providerId: string }) {
  const { t } = useI18n();
  const texts = useFileTexts();
  const query = useAdminProviderDocuments(providerId);
  return (
    <QueryView query={query} loading="detail">
      {(documents) =>
        documents.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="size-5" aria-hidden />
            {t.admin.providers.noDocuments}
          </p>
        ) : (
          <div className="grid gap-4">
            {PROVIDER_DOCUMENT_TYPES.filter((type) =>
              documents.some((document) => document.type === type),
            ).map((type) => (
              <div key={type} className="grid gap-2">
                <p className="text-sm font-medium">{t.provider.onboarding.docTypes[type]}</p>
                <FileGallery
                  items={documents.filter((document) => document.type === type).map(galleryItem)}
                  labels={texts.gallery}
                />
              </div>
            ))}
          </div>
        )
      }
    </QueryView>
  );
}
